import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Sunrise, FileText } from "lucide-react";

export default function JobSeekerAuth() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (error) throw error;
      
      toast.success("Welcome back! Let's continue your journey.");
      navigate("/candidate/dashboard");
    } catch (error: any) {
      toast.error(error.message || "Sign in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--jobseeker-bg))] flex flex-col">
      {/* Header */}
      <header className="p-6">
        <div className="flex items-center gap-2">
          <Sunrise className="w-8 h-8 text-[hsl(var(--jobseeker-blue))]" />
          <h1 className="text-2xl font-bold text-foreground">RePath</h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Hero Section */}
          <div className="text-center mb-8">
            <div className="relative w-full h-48 mb-6 rounded-lg overflow-hidden bg-gradient-to-r from-[hsl(var(--jobseeker-blue))] to-[hsl(var(--jobseeker-gold))] flex items-center justify-center">
              <div className="absolute inset-0 opacity-20">
                <svg viewBox="0 0 400 200" className="w-full h-full">
                  <path 
                    d="M0,100 Q100,50 200,100 T400,100" 
                    fill="none" 
                    stroke="white" 
                    strokeWidth="3"
                    opacity="0.5"
                  />
                  <circle cx="350" cy="40" r="30" fill="white" opacity="0.3" />
                </svg>
              </div>
              <div className="relative z-10 text-white">
                <Sunrise className="w-16 h-16 mx-auto mb-2" />
                <p className="text-sm font-medium">A New Beginning</p>
              </div>
            </div>
            
            <h2 className="text-3xl font-bold text-foreground mb-2">
              Your Next Chapter Starts Here
            </h2>
            <p className="text-muted-foreground">
              Log in to connect with employers who see your potential.
            </p>
          </div>

          {/* Login Form */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <form onSubmit={handleSignIn} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email or Username</Label>
                <Input
                  id="email"
                  type="text"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  placeholder="your@email.com"
                  className="border-border"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  placeholder="Enter your password"
                  className="border-border"
                  required
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="remember"
                    checked={rememberMe}
                    onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                  />
                  <Label htmlFor="remember" className="text-sm cursor-pointer">
                    Remember Me
                  </Label>
                </div>
                <a href="#" className="text-sm text-[hsl(var(--jobseeker-blue))] hover:underline">
                  Forgot Password?
                </a>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[hsl(var(--jobseeker-gold))] text-foreground hover:bg-[hsl(var(--jobseeker-gold))]/90 font-semibold py-6 text-lg shadow-md"
              >
                {loading ? "Signing in..." : "Continue"}
              </Button>
            </form>

            <div className="mt-6 space-y-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-muted-foreground">Or</span>
                </div>
              </div>

              <Button
                asChild
                variant="outline"
                className="w-full border-[hsl(var(--jobseeker-blue))] text-[hsl(var(--jobseeker-blue))] hover:bg-[hsl(var(--jobseeker-blue))]/10"
              >
                <Link to="/ai-resume-builder" className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Build AI Resume (No Account Needed)
                </Link>
              </Button>

              <p className="text-sm text-muted-foreground text-center">
                Don't have an account?{" "}
                <a href="/jobseeker-signup" className="text-[hsl(var(--jobseeker-blue))] hover:underline font-semibold">
                  Start Fresh
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="p-6 border-t border-border/50">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
          <a href="/privacy" className="hover:text-foreground transition-colors">
            Privacy Policy
          </a>
          <a href="/terms" className="hover:text-foreground transition-colors">
            Terms of Service
          </a>
          <a href="mailto:admin@sccapp.co.uk" className="hover:text-foreground transition-colors">
            Contact Support
          </a>
        </div>
      </footer>
    </div>
  );
}
